import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface FeaturedAd {
  id: string;
  company_name: string;
  offer_text: string;
  image_url?: string;
  cta_url?: string;
  category?: string;
  page_placement: string;
  target_locations?: string[];
}

export const useFeaturedAds = (nearestCrossing?: string) => {
  const [featuredAds, setFeaturedAds] = useState<FeaturedAd[]>([]);
  const [sponsoredTips, setSponsoredTips] = useState<FeaturedAd[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAds();
  }, [nearestCrossing]);

  const fetchAds = async () => {
    try {
      const today = new Date().toISOString();

      // Fetch featured crossing ads
      let featuredQuery = supabase
        .from('ads')
        .select('*')
        .eq('is_active', true)
        .eq('page_placement', 'featured_crossing')
        .lte('start_date', today)
        .gte('end_date', today)
        .limit(2);

      // Filter by location if provided
      if (nearestCrossing) {
        featuredQuery = featuredQuery.contains('target_locations', [nearestCrossing]);
      }

      const { data: featuredData } = await featuredQuery;

      // If no geo-targeted ads, fetch generic ones
      if (!featuredData || featuredData.length === 0) {
        const { data: genericData } = await supabase
          .from('ads')
          .select('*')
          .eq('is_active', true)
          .eq('page_placement', 'featured_crossing')
          .lte('start_date', today)
          .gte('end_date', today)
          .is('target_locations', null)
          .limit(2);
        
        setFeaturedAds(genericData || []);
      } else {
        setFeaturedAds(featuredData || []);
      }

      // Fetch sponsored tips
      const { data: tipsData } = await supabase
        .from('ads')
        .select('*')
        .eq('is_active', true)
        .eq('page_placement', 'sponsored_tip')
        .lte('start_date', today)
        .gte('end_date', today)
        .limit(1);

      setSponsoredTips(tipsData || []);

      // Fallback to PSAs if no ads
      if ((!featuredData || featuredData.length === 0) && (!tipsData || tipsData.length === 0)) {
        // Could fetch PSAs from content_management table here
        // For now, we'll let the UI handle fallback
      }
    } catch (error) {
      console.error('Error fetching featured ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissAd = (adId: string) => {
    setFeaturedAds(prev => prev.filter(ad => ad.id !== adId));
    setSponsoredTips(prev => prev.filter(ad => ad.id !== adId));
  };

  return {
    featuredAds,
    sponsoredTips,
    loading,
    dismissAd,
    refetch: fetchAds,
  };
};
